import { Alert, Group, IconButton, iconSizes, Text, Title } from '@common/ui'
import { IconHelpCircle } from '@common/ui/icons'
import React, { useState } from 'react'
import { com } from '@eidu/web'
import LabelLineDisplay from './LabelLineDisplay'
import FieldType = com.eidu.entity.field.FieldType
import Field = com.eidu.entity.field.Field
import EntityLabelLine = com.eidu.entity.label.EntityLabelLine

export type LabelDisplayProps = {
  fields: readonly Field[]
  primaryLabel: EntityLabelLine
  secondaryLabel: EntityLabelLine | undefined
}

const LabelDisplay = ({ fields, primaryLabel, secondaryLabel }: LabelDisplayProps) => {
  const [showInstructions, setShowInstructions] = useState(false)

  const availablePrimaryLabelFields: Field[] = fields.filter(
    (field) => field.type === FieldType.Text || field.type === FieldType.Number || field.type === FieldType.Date
  )

  const availableSecondaryLabelFields: Field[] = fields.filter(
    (field) =>
      field.type === FieldType.Text ||
      field.type === FieldType.Number ||
      field.type === FieldType.Date ||
      field.type === FieldType.Reference
  )

  return (
    <>
      <Group gap="xxs" align="center">
        <Title order={4}>Label</Title>
        <IconButton
          color="platform"
          aria-label="Show label instructions"
          onClick={() => setShowInstructions(!showInstructions)}
        >
          <IconHelpCircle size={iconSizes.sm} stroke={1.5} />
        </IconButton>
      </Group>

      {showInstructions && (
        <Alert severity="info" withCloseButton onClose={() => setShowInstructions(false)}>
          <Text component="div">
            To create an entity label, use curly braces <code>{}</code> to include the values of specific fields in your
            text. The label consists of two lines:
            <ol>
              <li>
                <strong>First Line:</strong> You can reference fields of type <i>Text</i>, <i>Number</i>, or <i>Date</i>
                . Simply type the text you want, and where you need the value of a field, enclose the field name in
                curly braces. For example, <code>&laquo;{'Name: {First Name}'}&raquo;</code> will display
                <i>&laquo;Name: &raquo;</i> followed by the value in the <code>First Name</code> field.
              </li>
              <li>
                <strong>Second Line:</strong> In addition to <i>Text</i>, <i>Number</i>, and <i>Date</i> fields, you may
                also reference <i>Reference</i> type fields here. Write your text, and use curly braces to include
                fields as needed. For example, <code>&laquo;{'{Linked Entity}'}&raquo;</code> would display the
                reference field <code>Linked Entity</code>.
              </li>
            </ol>
          </Text>

          <Text component="div">
            Ensure your field names match exactly, including capitalization, and enclose each field name within{' '}
            <code>{'{}'}</code> to ensure it renders correctly.
          </Text>
        </Alert>
      )}
      {availablePrimaryLabelFields.length === 0 && (
        <Text fw={700} mt="xs">
          No suitable fields found.
        </Text>
      )}

      <LabelLineDisplay
        label={primaryLabel}
        title="First Line (required)"
        availableFields={availablePrimaryLabelFields}
      />

      <LabelLineDisplay
        label={secondaryLabel}
        title="Second Line (optional)"
        availableFields={availableSecondaryLabelFields}
      />
    </>
  )
}

export default LabelDisplay
